// The projection screen's state, as URL search params.
//
// A scenario is a link: "two go-lives a month growing 5%, in USD, for Harris"
// is something a person sends before a planning meeting, and it must open the
// same way for whoever clicks it. So the window, the currency, the partner and
// every scenario override live in the URL, and the saved assumptions are only
// what a missing override falls back to.
//
// KEYS ARE NAMED TO NOT COLLIDE. TanStack Router types the search of EVERY
// route as one intersection, so a key spelled the same as another route's with
// a different type leaves neither screen a type to navigate with. `currency`,
// `partner_id` and `scope` match the other report screens exactly; the rest
// are prefixed `pj_` and appear nowhere else. Each field carries its own
// `.catch(undefined)`, so one unreadable param falls back alone.

import { z } from "zod";
import { Currency, ReportScope, Uuid } from "@crm/shared";
import {
  ASSUMPTION_KEYS,
  DEFAULT_PROJECTION_QUERY,
  assumptionProblem,
  type AssumptionKey,
  type ProjectionQueryState,
  type ScenarioOverrides,
} from "./projectionModel.js";

const IsoMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

function assumption(key: AssumptionKey) {
  return z.coerce
    .number()
    .refine((value) => assumptionProblem(key, value) === null)
    .optional()
    .catch(undefined);
}

/** URL key per assumption. Short, and prefixed so no other route can mean something else by it. */
export const SEARCH_KEY: Record<AssumptionKey, string> = {
  go_lives_per_month: "pj_golives",
  monthly_growth_pct: "pj_growth",
  margin_pct: "pj_margin",
  assumed_split_pct: "pj_split",
  implementation_lead_months: "pj_lead",
  implementation_kickoff_pct: "pj_kickoff",
};

const ProjectionSearchSchema = z.object({
  partner_id: Uuid.optional().catch(undefined),
  scope: ReportScope.optional().catch(undefined),
  pj_from: IsoMonth.optional().catch(undefined),
  pj_to: IsoMonth.optional().catch(undefined),
  pj_golives: assumption("go_lives_per_month"),
  pj_growth: assumption("monthly_growth_pct"),
  pj_margin: assumption("margin_pct"),
  pj_split: assumption("assumed_split_pct"),
  pj_lead: assumption("implementation_lead_months"),
  pj_kickoff: assumption("implementation_kickoff_pct"),
});

export type ProjectionSearch = z.infer<typeof ProjectionSearchSchema>;

export function validateProjectionSearch(raw: Record<string, unknown>): ProjectionSearch {
  const parsed = ProjectionSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

function overridesOf(search: ProjectionSearch): ScenarioOverrides {
  const overrides: ScenarioOverrides = {};
  for (const key of ASSUMPTION_KEYS) {
    const value = search[SEARCH_KEY[key] as keyof ProjectionSearch];
    if (typeof value === "number") overrides[key] = value;
  }
  return overrides;
}

export function searchToProjectionState(
  raw: ProjectionSearch,
  currency: Currency,
): ProjectionQueryState {
  const search = validateProjectionSearch(raw as Record<string, unknown>);
  return {
    currency,
    partnerId: search.partner_id ?? null,
    scope: search.scope ?? DEFAULT_PROJECTION_QUERY.scope,
    fromMonth: search.pj_from ?? null,
    // A window that ends before it starts is not a window; drop the end.
    toMonth:
      search.pj_to !== undefined && search.pj_from !== undefined && search.pj_to < search.pj_from
        ? null
        : (search.pj_to ?? null),
    overrides: overridesOf(search),
  };
}

/** Defaults omitted, so the saved scenario over the default window is a clean `/projections`. The currency is never written. */
export function projectionStateToSearch(state: ProjectionQueryState): ProjectionSearch {
  const search: Record<string, unknown> = {};
  if (state.partnerId !== null) {
    search["partner_id"] = state.partnerId;
    if (state.scope !== DEFAULT_PROJECTION_QUERY.scope) search["scope"] = state.scope;
  }
  if (state.fromMonth !== null) search["pj_from"] = state.fromMonth;
  if (state.toMonth !== null) search["pj_to"] = state.toMonth;
  for (const key of ASSUMPTION_KEYS) {
    const value = state.overrides[key];
    if (value !== undefined) search[SEARCH_KEY[key]] = value;
  }
  return search as ProjectionSearch;
}
